// Pure resize math for handle drags.
//
// Why this is its own module: the geometry is fiddly (edge anchors, Figma-
// style flip-through-zero, shift-locked aspect ratio, alt-key center-resize)
// and easier to reason about as a single pure function with focused tests
// than as logic embedded in Canvas.tsx.
//
// All inputs/outputs are world-space. Caller is responsible for converting
// the pointer position from stage coords to world coords first.

import type { WorldPoint } from './tools';

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ResizeOptions {
  handle: ResizeHandle;
  original: BBox;
  delta: WorldPoint;
  aspectLock: boolean;
  fromCenter: boolean;
  minSize: number;
}

// Which axes does this handle drag along.
export function handleDragsLeft(h: ResizeHandle): boolean {
  return h === 'nw' || h === 'w' || h === 'sw';
}
export function handleDragsRight(h: ResizeHandle): boolean {
  return h === 'ne' || h === 'e' || h === 'se';
}
export function handleDragsTop(h: ResizeHandle): boolean {
  return h === 'nw' || h === 'n' || h === 'ne';
}
export function handleDragsBottom(h: ResizeHandle): boolean {
  return h === 'sw' || h === 's' || h === 'se';
}

export function isCornerHandle(h: ResizeHandle): boolean {
  return h === 'nw' || h === 'ne' || h === 'sw' || h === 'se';
}

// Screen-space cursor for each handle, matching browser convention.
export function cursorForHandle(h: ResizeHandle): string {
  switch (h) {
    case 'nw':
    case 'se':
      return 'nwse-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'n':
    case 's':
      return 'ns-resize';
    case 'e':
    case 'w':
      return 'ew-resize';
  }
}

// Compute a new bbox from a handle drag.
//
// Behavior notes:
// - Corner drag past the opposite edge flips the bbox in that axis. The
//   shape keeps minSize on the flipped side rather than collapsing to zero.
// - aspectLock projects the cursor delta along the diagonal of the original
//   bbox so width/height stay proportional. Only meaningful on corners.
// - fromCenter mirrors the drag across the bbox center — both opposite
//   edges move outward at the same rate.
export function computeResize(opts: ResizeOptions): BBox {
  const { handle, original, aspectLock, fromCenter, minSize } = opts;
  const dragsLeft = handleDragsLeft(handle);
  const dragsRight = handleDragsRight(handle);
  const dragsTop = handleDragsTop(handle);
  const dragsBottom = handleDragsBottom(handle);

  let dx = opts.delta.x;
  let dy = opts.delta.y;

  // Aspect lock only makes sense on a corner where both axes move.
  if (aspectLock && isCornerHandle(handle) && original.w > 0 && original.h > 0) {
    const aspect = original.w / original.h;
    const signX = dragsRight ? 1 : -1;
    const signY = dragsBottom ? 1 : -1;
    // Project delta into "growth in width" / "growth in height" space.
    const widthGrowth = signX * dx;
    const heightGrowth = signY * dy;
    if (widthGrowth >= heightGrowth * aspect) {
      const heightFromWidth = widthGrowth / aspect;
      dy = signY * heightFromWidth;
    } else {
      const widthFromHeight = heightGrowth * aspect;
      dx = signX * widthFromHeight;
    }
  }

  // X axis.
  let newX = original.x;
  let newW = original.w;
  if (dragsLeft || dragsRight) {
    if (fromCenter) {
      const center = original.x + original.w / 2;
      const growth = dragsRight ? dx : -dx;
      let halfW = original.w / 2 + growth;
      if (Math.abs(halfW) < minSize / 2) {
        halfW = halfW >= 0 ? minSize / 2 : -minSize / 2;
      }
      newX = center - Math.abs(halfW);
      newW = Math.abs(halfW) * 2;
    } else {
      const anchorX = dragsLeft ? original.x + original.w : original.x;
      const activeX = dragsLeft ? original.x + dx : original.x + original.w + dx;
      let signed = activeX - anchorX;
      if (Math.abs(signed) < minSize) {
        signed = signed >= 0 ? minSize : -minSize;
      }
      const finalActive = anchorX + signed;
      newX = Math.min(anchorX, finalActive);
      newW = Math.abs(signed);
    }
  }

  // Y axis.
  let newY = original.y;
  let newH = original.h;
  if (dragsTop || dragsBottom) {
    if (fromCenter) {
      const center = original.y + original.h / 2;
      const growth = dragsBottom ? dy : -dy;
      let halfH = original.h / 2 + growth;
      if (Math.abs(halfH) < minSize / 2) {
        halfH = halfH >= 0 ? minSize / 2 : -minSize / 2;
      }
      newY = center - Math.abs(halfH);
      newH = Math.abs(halfH) * 2;
    } else {
      const anchorY = dragsTop ? original.y + original.h : original.y;
      const activeY = dragsTop ? original.y + dy : original.y + original.h + dy;
      let signed = activeY - anchorY;
      if (Math.abs(signed) < minSize) {
        signed = signed >= 0 ? minSize : -minSize;
      }
      const finalActive = anchorY + signed;
      newY = Math.min(anchorY, finalActive);
      newH = Math.abs(signed);
    }
  }

  return { x: newX, y: newY, w: newW, h: newH };
}

// Eight handles in render order. Corners first so they sit on top of
// overlapping edge handles when the bbox is tiny.
export const RESIZE_HANDLES: readonly ResizeHandle[] = [
  'n', 'e', 's', 'w', 'nw', 'ne', 'se', 'sw',
];

// Position (in shape-local coords) of each handle on a bbox of size w×h.
export function handleCenter(handle: ResizeHandle, w: number, h: number): WorldPoint {
  switch (handle) {
    case 'nw': return { x: 0, y: 0 };
    case 'n':  return { x: w / 2, y: 0 };
    case 'ne': return { x: w, y: 0 };
    case 'e':  return { x: w, y: h / 2 };
    case 'se': return { x: w, y: h };
    case 's':  return { x: w / 2, y: h };
    case 'sw': return { x: 0, y: h };
    case 'w':  return { x: 0, y: h / 2 };
  }
}
